"use client";

import React, { useState, useRef, useEffect, useCallback } from "react";
import { Search, X, RotateCcw, Filter, History, Keyboard, ArrowLeft, ChevronDown } from "lucide-react";
import { Button } from "./button";
import { Input } from "./input";
import { SearchFilters } from "./SearchFilters";
import { SearchResultGroupCollapsible } from "./SearchResultGroupCollapsible";
import { SearchResultGroupSidebar } from "./SearchResultGroupSidebar";
import { SearchSuggestions } from "./SearchSuggestions";
import { SearchLoadingSkeleton } from "./SearchLoadingSkeleton";
import { SearchHistoryDrawer } from "./SearchHistoryDrawer";
import { useSearchHistory } from "../hooks/useSearchHistory";

export interface SearchResult {
  objectID: string;
  title?: string;
  name?: string;
  description?: string;
  type: string;
  url: string;
  iconType: string;
  subtitle?: string;
  tags?: string[];
  highlightedTitle?: string | null;
  highlightedContent?: string | null;
  status?: string;
  priority?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface UseAlgoliaSearchReturn {
  query: string;
  results: SearchResult[];
  isLoading: boolean;
  hasPersistedResults: boolean;
  filters: any;
  facets: any;
  /**
   * Run a search. The return value is deliberately UNCONSTRAINED (`unknown`):
   * these components read results from the hook's state, while other callers
   * (Hub's command palette) await the matched rows. Pinning this to
   * `Promise<void>` made the richer signature un-assignable here.
   */
  searchAlgolia: (query: string, filters?: any) => Promise<unknown>;
  handleResultClick: (result: SearchResult, onClose?: () => void) => void;
  clearSearch: () => void;
  clearFilters: () => void;
  setFilters: (filters: any) => void;
  restoreSession: () => void;
  getHighlightedParts: (text: string) => Array<{text: string, isHighlighted: boolean}>;
  getResultIcon: (type: string) => React.ReactNode;
}

interface AdvancedFullPageSearchProps {
  useAlgoliaSearch: () => UseAlgoliaSearchReturn;
  placeholder?: string;
  showKeyboardShortcuts?: boolean;
  showBackButton?: boolean;
  showHistoryDrawer?: boolean;
  showAdvancedFilters?: boolean;
}

export function AdvancedFullPageSearch({
  useAlgoliaSearch,
  placeholder = "Search for tickets, contacts, time entries...",
  showKeyboardShortcuts = true,
  showBackButton = true,
  showHistoryDrawer = true,
  showAdvancedFilters = true,
}: AdvancedFullPageSearchProps) {
  const [showFilters, setShowFilters] = useState(true);
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [displayedResults, setDisplayedResults] = useState<SearchResult[]>([]);
  const [resultsPerPage] = useState(20);
  const [enabledGroups, setEnabledGroups] = useState<Record<string, boolean>>({});
  const inputRef = useRef<HTMLInputElement>(null);
  const { addToHistory } = useSearchHistory();
  
  const {
    query,
    results,
    isLoading,
    hasPersistedResults,
    filters,
    facets,
    searchAlgolia,
    handleResultClick,
    clearSearch,
    clearFilters,
    setFilters,
    restoreSession,
    getHighlightedParts,
    getResultIcon
  } = useAlgoliaSearch();

  // Load recent searches
  useEffect(() => {
    const stored = localStorage.getItem("recent_searches");
    if (stored) {
      try {
        setRecentSearches(JSON.parse(stored));
      } catch (error) {
        console.warn("Failed to load recent searches:", error);
      }
    }
  }, []);

  // Update displayed results when results change
  useEffect(() => {
    setDisplayedResults(results.slice(0, resultsPerPage));
  }, [results, resultsPerPage]);

  // Focus input on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
    if (!query && !results.length) {
      restoreSession();
    }
  }, [query, results.length, restoreSession]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case 'k':
            e.preventDefault();
            if (inputRef.current) {
              inputRef.current.focus();
            }
            break;
          case '/':
            e.preventDefault();
            setShowKeyboardHelp(!showKeyboardHelp);
            break;
        }
      }

      if (e.key === 'Escape') {
        if (query) {
          clearSearch();
        } else if (showKeyboardHelp) {
          setShowKeyboardHelp(false);
        } else if (showFilters) {
          setShowFilters(false);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [query, clearSearch, showKeyboardHelp, showFilters]);

  const handleSearch = useCallback((value: string) => {
    searchAlgolia(value, filters);
    if (value.trim()) {
      const updated = [value, ...recentSearches.filter(s => s !== value)].slice(0, 10);
      setRecentSearches(updated);
      localStorage.setItem("recent_searches", JSON.stringify(updated));
    }
  }, [searchAlgolia, filters, recentSearches]);

  const handleResultClickInternal = useCallback((result: SearchResult) => {
    addToHistory({
      id: result.objectID,
      title: result.title || result.name || "Result",
      type: result.type,
      url: result.url,
      subtitle: result.subtitle,
      searchQuery: query
    });
    
    handleResultClick(result);
  }, [handleResultClick, addToHistory, query]);

  const renderHighlightedText = useCallback((text: string) => {
    const parts = getHighlightedParts(text);
    
    return (
      <span>
        {parts.map((part, index) => {
          if (part.isHighlighted) {
            return (
              <mark key={index} className="bg-yellow-200 text-yellow-900 px-1 rounded">
                {part.text}
              </mark>
            );
          }
          return <span key={index}>{part.text}</span>;
        })}
      </span>
    );
  }, [getHighlightedParts]);

  const hasActiveFilters = Object.values(filters).some(
    v => Array.isArray(v) && v.length > 0
  );

  return (
    <div className="flex flex-col h-full min-h-screen bg-white">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white sticky top-0 z-10 flex-shrink-0">
        <div className="px-4 py-4 space-y-4">
          {/* Top bar with back button and title */}
          <div className="flex items-center justify-between">
            {showBackButton && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.history.back()}
                className="mr-2"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <h1 className="text-2xl font-bold text-gray-900">
              Search
            </h1>
            <div className="flex items-center space-x-1">
              {showAdvancedFilters && (
                <Button
                  variant={hasActiveFilters ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className="relative"
                >
                  <Filter className="h-4 w-4" />
                  {hasActiveFilters && (
                    <span className="absolute top-0 right-0 h-2 w-2 bg-blue-600 rounded-full" />
                  )}
                </Button>
              )}
              {showKeyboardShortcuts && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowKeyboardHelp(!showKeyboardHelp)}
                >
                  <Keyboard className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Search input */}
          <div className="relative">
            <Input
              ref={inputRef}
              placeholder={placeholder}
              value={query}
              onChange={(e) => handleSearch(e.target.value)}
              className="text-base pr-20"
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center space-x-1">
              {query && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearSearch}
                  className="h-6 w-6 p-0 hover:bg-gray-100"
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
              {hasPersistedResults && !query && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={restoreSession}
                  className="h-6 w-6 p-0 hover:bg-gray-100"
                  title="Restore previous search"
                >
                  <RotateCcw className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          {/* Active filters display */}
          {hasActiveFilters && (
            <div className="flex items-center space-x-2 flex-wrap">
              <span className="text-sm text-gray-600">Active filters:</span>
              {Object.entries(filters).map(([key, values]) => {
                if (!Array.isArray(values) || values.length === 0) return null;
                return values.map(value => (
                  <span
                    key={`${key}-${value}`}
                    className="inline-flex items-center space-x-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs"
                  >
                    <span>{value}</span>
                    <button type="button"
                      onClick={() => {
                        const newValues = values.filter(v => v !== value);
                        setFilters({
                          ...filters,
                          [key]: newValues.length > 0 ? newValues : undefined,
                        });
                      }}
                      className="hover:text-blue-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ));
              })}
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-xs text-gray-600 hover:text-gray-900"
              >
                Clear all
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden min-h-0">
        {/* Filters sidebar */}
        {showAdvancedFilters && showFilters && (
          <div className="w-64 flex-shrink-0 border-r border-gray-200 overflow-y-auto h-full">
            <SearchFilters
              facets={facets}
              selectedFilters={filters}
              onFilterChange={setFilters}
              onClearFilters={clearFilters}
            />
          </div>
        )}

        {/* Group filters sidebar */}
        <SearchResultGroupSidebar
          results={displayedResults}
          onGroupToggle={(type, enabled) => {
            setEnabledGroups(prev => ({
              ...prev,
              [type]: enabled,
            }));
          }}
          enabledGroups={enabledGroups}
          getResultIcon={getResultIcon}
        />

        {/* Results area */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {isLoading && (
            <SearchLoadingSkeleton count={5} />
          )}

          {!isLoading && displayedResults.length > 0 && (
            <div className="space-y-6 px-6 py-4">
              <SearchResultGroupCollapsible
                results={displayedResults}
                getResultIcon={getResultIcon}
                renderHighlightedText={renderHighlightedText}
                onResultClick={handleResultClickInternal}
                enabledGroups={enabledGroups}
              />

              {/* Load more button */}
              {results.length > resultsPerPage && displayedResults.length < results.length && (
                <div className="flex justify-center py-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setDisplayedResults(results.slice(0, displayedResults.length + resultsPerPage));
                    }}
                  >
                    <ChevronDown className="h-4 w-4 mr-2" />
                    Load more ({results.length - displayedResults.length} remaining)
                  </Button>
                </div>
              )}

              {/* Results summary */}
              <div className="text-center text-sm text-gray-500 py-4">
                Showing {displayedResults.length} of {results.length} results
              </div>
            </div>
          )}

          {!isLoading && query && results.length === 0 && (
            <SearchSuggestions
              query={query}
              onSuggestionClick={handleSearch}
            />
          )}

          {!isLoading && !query && results.length === 0 && (
            <SearchSuggestions
              query=""
              onSuggestionClick={handleSearch}
            />
          )}
        </div>

        {/* History sidebar */}
        {showHistoryDrawer && (
          <div className="w-64 border-l border-gray-200 overflow-y-auto">
            <SearchHistoryDrawer
              onItemClick={(item) => {
                window.location.href = item.url;
              }}
              className="h-full border-0"
            />
          </div>
        )}
      </div>

      {/* Keyboard help modal */}
      {showKeyboardHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md">
            <h2 className="text-lg font-bold mb-4">Keyboard Shortcuts</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Focus search</span>
                <kbd className="px-2 py-1 bg-gray-100 rounded">Cmd/Ctrl + K</kbd>
              </div>
              <div className="flex justify-between">
                <span>Clear search</span>
                <kbd className="px-2 py-1 bg-gray-100 rounded">Esc</kbd>
              </div>
              <div className="flex justify-between">
                <span>Show shortcuts</span>
                <kbd className="px-2 py-1 bg-gray-100 rounded">Cmd/Ctrl + /</kbd>
              </div>
            </div>
            <Button
              variant="outline"
              className="w-full mt-4"
              onClick={() => setShowKeyboardHelp(false)}
            >
              Close
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
